import React, { useState, useEffect, useRef, useMemo } from 'react';
import mapboxgl from 'mapbox-gl';
import { createRoute, updateRoute } from '../../../services/routeService';
import { RouteGenerationService } from '../../../services/routeGenerationService';
import supabase from '../../../services/supabase';
import { getMapboxToken, comprehensiveSearch, reverseGeocode } from '../../../services/mapService';
import FullscreenMapButton from './FullscreenMapButton';
import RouteCollaboratorsModal from './RouteCollaboratorsModal';
import { addCollaborator, listCollaborators } from '../../../services/collaboratorService';
import './RouteCreation.css';

// Progress indicator component
const RouteProgressIndicator = ({ currentStep, totalSteps, onStepClick, stepMeta = {}, pointsSummary = [], title = 'Create Your Route' }) => {
  const [pointsOpen, setPointsOpen] = React.useState(pointsSummary.length <= 4);
  const steps = [
    { id: 1, label: 'Route Name', icon: '📝', sectionId: 'route-name-section' },
    { id: 2, label: 'Add Points', icon: '📍', sectionId: 'points-section' }
  ];

  return (
    <div className="route-progress">
      <div className="progress-header">
        <h3>{title}</h3>
        <span className="progress-text">Step {currentStep} of {totalSteps}</span>
      </div>
      <div className="progress-steps">
        {steps.map((step, index) => (
          <div key={step.id}>
            <div
              className={`progress-step ${index + 1 <= currentStep ? 'active' : ''} ${index + 1 < currentStep ? 'completed' : ''}`}
              onClick={() => {
                onStepClick(step.sectionId);
              }}
              style={{ cursor: 'pointer' }}
            >
              <div className="step-icon">
                {index + 1 < currentStep ? '✓' : step.icon}
              </div>
              <span className="step-label">{step.label}</span>
              {stepMeta[step.id]?.badges && (
                <div className="step-meta">
                  {stepMeta[step.id].badges.map((b, i) => (
                    <span key={i} className={`step-badge ${b.variant || ''}`}>{b.text}</span>
                  ))}
                </div>
              )}
              {step.id === 2 && (
                <span
                  className={`progress-chevron ${pointsOpen ? 'open' : ''}`}
                  onClick={(e) => { e.stopPropagation(); setPointsOpen(v => !v); }}
                  role="button"
                  aria-expanded={pointsOpen}
                  aria-label={pointsOpen ? 'Collapse points' : 'Expand points'}
                >
                  {pointsOpen ? '▾' : '▸'}
                </span>
              )}
              {index < steps.length - 1 && <div className="step-connector" />}
            </div>
            {step.id === 2 && pointsSummary && pointsSummary.length > 0 && (
              <div className="progress-sub">
                {pointsOpen && (
                  <div id="progress-points-list" className="progress-sublist" role="list">
                    {pointsSummary.map((txt, i) => (
                      <div key={i} className="progress-subitem" role="listitem">
                        <span className="subindex">{i + 1}.</span>
                        <span className="subtext">{txt}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const RouteCreation = ({ userId, onCancel, mapContainerRef, mapRef, onCreated, mode = 'create', initialRoute = null, initialPoints = [], onSaveEdit }) => {

  // Refs for section navigation
  const routeNameSectionRef = useRef(null);
  const pointsSectionRef = useRef(null);
  const generationSectionRef = useRef(null);
  const previewSectionRef = useRef(null);
  const routeFormRef = useRef(null);
  const markersRef = useRef([]);
  // UI element refs to ensure dropdowns stay in view
  const costInlineRefs = useRef([]);
  const tagsInlineRefs = useRef([]);
  const stayInlineRefs = useRef([]);
  
  // Refs for popup management
  const popupRefs = useRef(new Map()); // Store popup instances by point index
  const geocodingInProgress = useRef(new Set()); // Track which points are being geocoded

  // Creator profile (for avatar in route name meta)
  const [creatorAvatarUrl, setCreatorAvatarUrl] = useState(null);
  const [creatorUsername, setCreatorUsername] = useState('');

  // State for route creation
  const [routeName, setRouteName] = useState('');
  const [routeDate, setRouteDate] = useState('');
  const [routeStartDate, setRouteStartDate] = useState('');
  const [routeEndDate, setRouteEndDate] = useState('');
  const [routePoints, setRoutePoints] = useState([{ name: '', location: '', tags: [], notes: '', coordinates: null, costCents: 0 }]);
  const [isPrivate, setIsPrivate] = useState(false);
  const [searchSuggestions, setSearchSuggestions] = useState({});
  const [showSuggestions, setShowSuggestions] = useState({});
  const [activeTab, setActiveTab] = useState('Plan');
  const tabs = ['Plan'];
  
  // Route generation state
  const [transportMode, setTransportMode] = useState('driving');
  const [routeGeometry, setRouteGeometry] = useState(null);
  const [routeLegDurations, setRouteLegDurations] = useState([]); // seconds per leg
  const [openLegDirections, setOpenLegDirections] = useState({}); // map of legIndex->boolean
  const [generatingRoute, setGeneratingRoute] = useState(false);
  const [routePreviewMode, setRoutePreviewMode] = useState(false);
  const [savingRoute, setSavingRoute] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [expandedSections, setExpandedSections] = useState({
    routeName: true,
    points: true,
    generation: false,
    preview: false
  });
  // Collaborators state
  const [collabModalOpen, setCollabModalOpen] = useState(false);
  const [collaborators, setCollaborators] = useState([]);

  // Sanitize notes to prevent spammy/abusive content
  const sanitizeNotes = (text) => {
    let s = text ?? '';
    // Normalize whitespace and newlines
    s = s.replace(/[ \t]+/g, ' ').replace(/\r\n/g, '\n').trim();
    // Collapse 3+ blank lines to 2
    s = s.replace(/\n{3,}/g, '\n\n');
    // Cap consecutive identical characters to 3
    s = s.replace(/(.)\1{3,}/g, '$1$1$1');
    // Limit number of lines to 20
    const lines = s.split('\n').slice(0, 20);
    s = lines.join('\n');
    // Enforce max length
    return s.slice(0, 500);
  };

  // Derived meta for progress navbar badges
  // Progress badges removed for cleaner UI
  // (previously showed counts for points, times, tags, and notes)

  // Scrollspy: update current step based on section positions in left panel
  useEffect(() => {
    const container = routeFormRef.current;
    if (!container) return;
    const sections = [
      { ref: routeNameSectionRef, step: 1 },
      { ref: pointsSectionRef, step: 2 },
      { ref: generationSectionRef, step: 3 },
      { ref: previewSectionRef, step: 4 }
    ];
    const onScroll = () => {
      const cTop = container.getBoundingClientRect().top;
      let active = 1;
      for (const s of sections) {
        const el = s.ref.current;
        if (!el) continue;
        const r = el.getBoundingClientRect();
        if (r.top - cTop <= 40) active = s.step;
      }
      setCurrentStep(active);
    };
    container.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => container.removeEventListener('scroll', onScroll);
  }, [routeFormRef.current]);

  // Time utilities for 30-min interval time range selection
  const timeOptions = useMemo(() => {
    const labels = [];
    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m += 30) {
        const hour12 = ((h + 11) % 12) + 1;
        const min = m.toString().padStart(2, '0');
        const ampm = h < 12 ? 'AM' : 'PM';
        labels.push(`${hour12}:${min} ${ampm}`);
      }
    }
    return labels;
  }, []);

  const parseTimeLabelToMinutes = (label) => {
    if (!label) return null;
    try {
      const [time, ampm] = label.split(' ');
      const [hh, mm] = time.split(':').map(Number);
      let h24 = hh % 12;
      if (ampm === 'PM') h24 += 12;
      return h24 * 60 + (mm || 0);
    } catch (_) {
      return null;
    }
  };
  const minutesToClock = (m) => {
    if (m == null || isNaN(m)) return null;
    const hh = Math.floor(m / 60) % 24;
    const mm = m % 60;
    const ampm = hh < 12 ? 'AM' : 'PM';
    const h12 = ((hh + 11) % 12) + 1;
    return `${h12}:${String(mm).padStart(2, '0')} ${ampm}`;
  };

  // Money format/parsing (USD simple)
  const formatCurrency = (cents) => {
    try {
      const n = Math.max(0, Math.round(Number(cents) || 0)) / 100;
      return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    } catch (_) {
      return '$0.00';
    }
  };
  const parseAmountToCents = (val) => {
    if (typeof val === 'number') return Math.round(val * 100);
    const cleaned = String(val || '').replace(/[^0-9.\-]/g, '');
    const num = Number(cleaned);
    return Number.isFinite(num) ? Math.round(num * 100) : 0;
  };

  // Preload state when editing (top-level, not inside JSX)
  useEffect(() => {
    if (mode !== 'edit' || !initialRoute) return;
    try {
      setRouteName(initialRoute.name || '');
      setIsPrivate(!!initialRoute.is_private);
      const mapped = (initialPoints || []).map((pt) => ({
        name: pt.name || '',
        location: pt.location_search || '',
        coordinates: pt.coordinates ? pt.coordinates : (typeof pt.longitude === 'number' && typeof pt.latitude === 'number' ? [pt.longitude, pt.latitude] : null),
        tags: Array.isArray(pt.tags) ? pt.tags : [],
        notes: pt.notes || '',
        costCents: typeof pt.cost_cents === 'number' ? pt.cost_cents : 0,
        stayDuration: typeof pt.stay_duration === 'number' ? pt.stay_duration : 0,
        stayStart: typeof pt.stay_start_min === 'number' ? minutesToClock(pt.stay_start_min) : null,
        stayEnd: typeof pt.stay_end_min === 'number' ? minutesToClock(pt.stay_end_min) : null,
      }));
      setRoutePoints(mapped.length > 0 ? mapped : [{ name: '', location: '', tags: [], notes: '', coordinates: null, costCents: 0 }]);
    } catch (_) {}
  }, [mode, initialRoute, initialPoints]);

  // Participants helpers (for cost split)
  const getParticipantCandidates = () => {
    const list = [];
    if (userId) list.push({ id: userId, name: creatorUsername || 'You', isSelf: true });
    (collaborators || []).forEach(c => {
      if (c?.user_id && c.user_id !== userId) {
        list.push({ id: c.user_id, name: c.username || c.full_name || c.name || (c.user_id?.slice(0, 6) || 'User') });
      }
    });
    return list;
  };
  const computeEqualShares = (totalCents, participantIds = [], payerId) => {
    const ids = Array.from(new Set(participantIds.filter(Boolean)));
    if (ids.length === 0) return {};
    const base = Math.floor((Math.max(0, Number(totalCents) || 0)) / ids.length);
    let remainder = Math.max(0, Number(totalCents) || 0) - base * ids.length;
    const shares = {};
    ids.forEach(id => { shares[id] = base; });
    const preferred = payerId && ids.includes(payerId) ? payerId : ids[0];
    shares[preferred] = (shares[preferred] || 0) + remainder;
    remainder = 0;
    return shares;
  };



  // Add state preservation for tab switching
  const [isPageVisible, setIsPageVisible] = useState(true);
  const [mapInitialized, setMapInitialized] = useState(false);

  // Debug useEffect for travel segments - commented out for cleaner console
  /*
  useEffect(() => {
    console.group('Travel Segments Debug');
    console.log('routePoints:', routePoints);
    
    // Log each point's structure
    routePoints?.forEach((point, index) => {
      console.log(`Point ${index}:`, {
        name: point.name,
        location: point.location,
        coordinates: point.coordinates,
        coordinatesType: typeof point.coordinates,
        hasCoordinates: !!point.coordinates
      });
    });
    
    const pointsWithCoords = (routePoints ?? []).filter(
      p => !!p?.coordinates && (
        (Array.isArray(p.coordinates) && p.coordinates.length === 2) ||
        (typeof p.coordinates?.lat === 'number' && typeof p.coordinates?.lng === 'number')
      )
    );
    console.log('pointsWithCoords count:', pointsWithCoords.length);
    console.log('Total points:', routePoints?.length ?? 0);
    console.groupEnd();
  }, [routePoints]);
  */

  // State preservation for tab switching
  const saveStateToStorage = () => {
    try {
      console.debug('[RouteCreation] Rendering markers. Total points:', routePoints.length);
      const stateToSave = {
        routeName,
        routeDate,
        routeStartDate,
        routeEndDate,
        routePoints,
        isPrivate,
        transportMode,
        currentStep,
        expandedSections,
        timestamp: Date.now()
      };
      localStorage.setItem('routeCreationState', JSON.stringify(stateToSave));
    } catch (e) {
      console.warn('Could not save state to localStorage:', e);
    }
  };

  const loadStateFromStorage = () => {
    try {
      const savedState = localStorage.getItem('routeCreationState');
      if (savedState) {
        const parsedState = JSON.parse(savedState);
        // Only restore if saved within last 30 minutes
        if (Date.now() - parsedState.timestamp < 30 * 60 * 1000) {
          setRouteName(parsedState.routeName || '');
          setRouteDate(parsedState.routeDate || '');
          setRouteStartDate(parsedState.routeStartDate || '');
          setRouteEndDate(parsedState.routeEndDate || '');
          setRoutePoints(parsedState.routePoints || [{ name: '', location: '', tags: [], notes: '', coordinates: null }]);
          setIsPrivate(parsedState.isPrivate || false);
          setTransportMode(parsedState.transportMode || 'driving');
          setCurrentStep(parsedState.currentStep || 1);
          setExpandedSections(parsedState.expandedSections || {
            routeName: true,
            points: true,
            generation: false,
            preview: false
          });
        }
      }
    } catch (e) {
      console.warn('Could not load state from localStorage:', e);
    }
  };

  const clearStateFromStorage = () => {
    try {
      localStorage.removeItem('routeCreationState');
    } catch (e) {
      console.warn('Could not clear state from localStorage:', e);
    }
  };

  // Wrapper for cancel action that clears stored state
  const handleCancel = () => {
    clearStateFromStorage();
    onCancel();
  };

  // Page visibility API to prevent reloads when switching tabs
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsPageVisible(!document.hidden);
    };

    const handlePageShow = (event) => {
      // Prevent page reload on tab switch
      if (event.persisted) {
        event.preventDefault();
      }
      setIsPageVisible(true);
    };

    const handlePageHide = (event) => {
      // Prevent page reload on tab switch
      if (event.persisted) {
        event.preventDefault();
      }
      setIsPageVisible(false);
    };

    // Add event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('pagehide', handlePageHide);

    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, []);

  // Load state from storage on component mount
  useEffect(() => {
    loadStateFromStorage();
  }, []);

  // Save state to storage when form data changes
  useEffect(() => {
    if (routeName || routePoints.some(p => p.name || p.location)) {
      saveStateToStorage();
    }
  }, [routeName, routeDate, routeStartDate, routeEndDate, routePoints, isPrivate, transportMode, currentStep, expandedSections]);

  // Prevent page reload on tab switch
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      // Only show warning if user has unsaved changes
      if (routeName || routePoints.some(p => p.name || p.location)) {
        event.preventDefault();
        event.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [routeName, routePoints]);

  // Point management functions
  const addPoint = () => {
    setRoutePoints([...routePoints, { 
      name: '', 
      location: '', 
      tags: [], 
      notes: '', 
      coordinates: null,
      costCents: 0,
      stayDuration: 60 // Default 1 hour stay
    }]);
  };

  const removePoint = (indexToRemove) => {
    if (routePoints.length > 1) {
      const updatedPoints = routePoints.filter((_, index) => index !== indexToRemove);
      setRoutePoints(updatedPoints);
      
      // Check if we now have fewer than 2 valid points and clear route if needed
      const validPoints = updatedPoints.filter(point => point.coordinates && Array.isArray(point.coordinates));
      if (validPoints.length < 2) {
        setRouteGeometry(null);
        setRoutePreviewMode(false);
        clearRouteFromMap();
      }
    }
  };

  const updatePoint = (index, field, value) => {
    const updatedPoints = [...routePoints];
    updatedPoints[index][field] = value;
    setRoutePoints(updatedPoints);
  };

  // Function to scroll to section when progress step is clicked
  const handleStepClick = (sectionId) => {
    let targetRef = null;
    
    switch (sectionId) {
      case 'route-name-section':
        targetRef = routeNameSectionRef;
        break;
      case 'points-section':
        targetRef = pointsSectionRef;
        break;

      case 'generation-section':
        targetRef = generationSectionRef;
        break;
      case 'preview-section':
        targetRef = previewSectionRef;
        break;
      default:
        return;
    }

    if (targetRef && targetRef.current) {
      targetRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const toggleTag = (pointIndex, tag) => {
    const updatedPoints = [...routePoints];
    const currentTags = updatedPoints[pointIndex].tags || [];
    
    if (currentTags.includes(tag)) {
      updatedPoints[pointIndex].tags = currentTags.filter(t => t !== tag);
    } else {
      updatedPoints[pointIndex].tags = [...currentTags, tag];
    }
    
    setRoutePoints(updatedPoints);
  };

  const toggleTagsDropdown = (pointIndex) => {
    const willOpen = !routePoints[pointIndex]?.tagsDropdownOpen;
    const updatedPoints = routePoints.map((p, i) => {
      if (!p) return p;
      if (i === pointIndex) {
        return { ...p, tagsDropdownOpen: willOpen, stayDropdownOpen: false, costDropdownOpen: false };
      }
      return { ...p, tagsDropdownOpen: false };
    });
    setRoutePoints(updatedPoints);
    if (willOpen) {
      try {
        const el = tagsInlineRefs.current[pointIndex];
        if (el && typeof el.scrollIntoView === 'function') {
          requestAnimationFrame(() => {
            try { el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' }); } catch (_) {}
          });
        }
      } catch (_) {}
    }
  };

  // Close all tag dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target;
      const clickedInsideOldTags = target.closest && target.closest('.tags-row');
      const clickedInsideInlineTags = target.closest && target.closest('.tags-inline');
      const clickedInsideTagsDropdown = target.closest && target.closest('.tags-dropdown-list');
      const clickedInsideStay = target.closest && target.closest('.stay-inline');
      const clickedInsideStayDropdown = target.closest && target.closest('.stay-dropdown');
      const clickedInsideCost = target.closest && target.closest('.cost-inline');
      const clickedInsideCostDropdown = target.closest && target.closest('.cost-dropdown');
      if (!clickedInsideOldTags && !clickedInsideInlineTags && !clickedInsideTagsDropdown && !clickedInsideStay && !clickedInsideStayDropdown && !clickedInsideCost && !clickedInsideCostDropdown) {
        const updatedPoints = routePoints.map(point => ({
          ...point,
          tagsDropdownOpen: false,
          stayDropdownOpen: false,
          costDropdownOpen: false
        }));
        setRoutePoints(updatedPoints);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [routePoints]);

  // Location search functions
  // Initialize Mapbox token once
  useEffect(() => {
    const token = getMapboxToken();
    if (token) {
      try {
        mapboxgl.accessToken = token;
      } catch (e) {
        console.error('Failed to set Mapbox token:', e);
      }
    } else {
      console.error('Mapbox token not found');
    }
  }, []);

  // Fetch creator profile image and username
  useEffect(() => {
    const fetchCreatorProfile = async () => {
      try {
        if (!userId) return;
        const { data, error } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('user_id', userId)
          .single();
        if (error) {
          return;
        }
        if (data) {
          const avatar = data.avatar_url || data.profilePicture || data.photo_url || null;
          setCreatorAvatarUrl(avatar);
          setCreatorUsername(data.username || data.full_name || '');
        }
      } catch (e) {
        // Non-fatal: leave placeholder
      }
    };
    fetchCreatorProfile();
  }, [userId]);

  // Helper to refresh collaborators after create
  const refreshCollaborators = async (routeId) => {
    try {
      if (!routeId) return;
      const rows = await listCollaborators(routeId);
      setCollaborators(rows);
    } catch (_) {}
  };

  const searchLocations = async (query, pointIndex) => {
    if (!query || query.length < 3) {
      setSearchSuggestions({ ...searchSuggestions, [pointIndex]: [] });
      setShowSuggestions({ ...showSuggestions, [pointIndex]: false });
      return;
    }

    try {
      // Get current map center for proximity bias
      let proximity = '';
      if (mapRef.current && mapRef.current.getCenter) {
        const center = mapRef.current.getCenter();
        proximity = `${center.lng},${center.lat}`;
      }

      const suggestions = await comprehensiveSearch(query, {
        proximity,
        limit: 8
      });

      setSearchSuggestions({ ...searchSuggestions, [pointIndex]: suggestions });
      setShowSuggestions({ ...showSuggestions, [pointIndex]: true });
    } catch (error) {
      console.error('Error searching locations:', error);
      setSearchSuggestions({ ...searchSuggestions, [pointIndex]: [] });
      setShowSuggestions({ ...showSuggestions, [pointIndex]: false });
    }
  };

  const handleLocationChange = (index, value) => {
    updatePoint(index, 'location', value);
    
    // Debounce the search
    setTimeout(() => {
      if (routePoints[index]?.location === value) {
        searchLocations(value, index);
      }
    }, 300);
  };

  const selectSuggestion = (index, suggestion) => {
    updatePoint(index, 'location', suggestion.place_name);
    updatePoint(index, 'name', suggestion.place_name.split(',')[0] || '');
    updatePoint(index, 'coordinates', suggestion.center);
    setShowSuggestions({ ...showSuggestions, [index]: false });
    
    // Zoom to selected location on the map
    if (mapRef.current && suggestion.center) {
      if (mapRef.current.loaded()) {
        mapRef.current.flyTo({ center: suggestion.center, zoom: 15 });
      } else {
        mapRef.current.once('load', () => {
          mapRef.current.flyTo({ center: suggestion.center, zoom: 15 });
        });
      }
    }
  };

  // Map click handler
  const handleMapClick = async (e) => {
    try { console.debug('[RouteCreation] Map click at', e?.lngLat); } catch (_) {}
    // Ignore clicks that originate from markers or popups
    try {
      const target = e?.originalEvent?.target;
      const path = (e?.originalEvent?.composedPath && e.originalEvent.composedPath()) || [];
      const isMarkerOrPopup = (el) => {
        try {
          if (!el) return false;
          if (el.classList?.contains('mapboxgl-marker') || el.closest?.('.mapboxgl-marker')) return true;
          if (el.classList?.contains('mapboxgl-popup') || el.closest?.('.mapboxgl-popup')) return true;
          return false;
        } catch (_) {
          return false;
        }
      };
      if (isMarkerOrPopup(target) || path.some(isMarkerOrPopup)) {
        console.debug('[RouteCreation] Map click ignored (marker/popup click)');
        return;
      }
    } catch (_) {}
    if (routePoints.length > 0) {
      const pointIndex = routePoints.findIndex(point => !point.coordinates);
      
      if (pointIndex !== -1) {
        const clickLng = e.lngLat.lng;
        const clickLat = e.lngLat.lat;
        
        // Add temporary visual feedback
        const tempMarker = document.createElement('div');
        tempMarker.className = 'temp-click-marker';
        tempMarker.innerHTML = '📍';
        
        const tempMapboxMarker = new mapboxgl.Marker({
          element: tempMarker,
          anchor: 'center'
        })
          .setLngLat([clickLng, clickLat])
          .addTo(mapRef.current);
        
        // Remove temp marker after 2 seconds
        setTimeout(() => {
          tempMapboxMarker.remove();
        }, 2000);
        
        // Get location name using reverse geocoding
        let locationName = '';
        try {
          console.log(`Attempting to reverse geocode coordinates: (${clickLat}, ${clickLng})`);
          
          // Test if reverseGeocode function is available
          if (typeof reverseGeocode !== 'function') {
            throw new Error('reverseGeocode function is not available');
          }
          
          // Test if Mapbox token is available
          const token = getMapboxToken();
          if (!token) {
            throw new Error('Mapbox token is missing');
          }
          console.log('🔑 Mapbox token available:', token ? 'Yes' : 'No');
          
          const locationData = await reverseGeocode(clickLat, clickLng);
          console.log('📍 Raw location data received:', locationData);
          
          if (locationData && locationData.name) {
            locationName = locationData.name;
            console.log(`✅ Reverse geocoded location: ${locationName} at (${clickLat}, ${clickLng})`);
          } else {
            throw new Error('Location data received but no name found');
          }
        } catch (error) {
          console.error('❌ Could not get location name:', error);
          locationName = `Location ${pointIndex + 1}`;
        }
        
        const updatedPoints = [...routePoints];
        updatedPoints[pointIndex].coordinates = [
          parseFloat(clickLng.toFixed(8)), 
          parseFloat(clickLat.toFixed(8))
        ];
        updatedPoints[pointIndex].name = locationName; // Set the location name
        
        console.log(`📍 Updated point ${pointIndex}:`, {
          name: updatedPoints[pointIndex].name,
          coordinates: updatedPoints[pointIndex].coordinates,
          fullPoint: updatedPoints[pointIndex]
        });
        
        setRoutePoints(updatedPoints);
        
        // Auto-add next point if needed (max 8 points)
        const remainingEmptyPoints = updatedPoints.filter(point => !point.coordinates);
        if (remainingEmptyPoints.length === 0 && updatedPoints.length < 8) {
          setTimeout(() => {
            setRoutePoints(prev => [...prev, { 
              name: '', 
              location: '', 
              tags: [], 
              notes: '', 
              coordinates: null 
            }]);
          }, 100);
        }
      }
    }
  };

  // Set up map click handler with better cleanup
  useEffect(() => {
    if (mapRef.current && isPageVisible) {
      mapRef.current.on('click', handleMapClick);
      return () => {
        if (mapRef.current) {
          mapRef.current.off('click', handleMapClick);
        }
      };
    }
  }, [mapRef.current, routePoints, isPageVisible]);

  // Better map cleanup on component unmount
  useEffect(() => {
    return () => {
      // Clean up markers
      if (Array.isArray(markersRef.current)) {
        markersRef.current.forEach(marker => {
          try {
            if (marker && marker.remove) {
              marker.remove();
            }
          } catch (e) {
            console.warn('Error removing marker:', e);
          }
        });
        markersRef.current = [];
      }
      
      // Clean up popups
      popupRefs.current.forEach((popup, index) => {
        try {
          popup.remove();
        } catch (e) {
          console.warn('Error removing popup:', e);
        }
      });
      popupRefs.current.clear();
      geocodingInProgress.current.clear();

      // Clean up map sources and layers
      if (mapRef.current) {
        try {
          if (mapRef.current.getSource('generated-route')) {
            mapRef.current.removeLayer('generated-route-line');
            mapRef.current.removeSource('generated-route');
          }
        } catch (e) {
          console.warn('Error cleaning up map sources:', e);
        }
      }

      // Clear stored state on unmount
      clearStateFromStorage();
    };
  }, []);

  // Function to update existing points with location names - no longer needed with new approach

  // Render and maintain persistent markers/popups without recreating on every state change
  useEffect(() => {
    if (!mapRef.current || !isPageVisible) return;

    try {
      // Ensure markersRef.current is an array for index-based reuse
      if (!Array.isArray(markersRef.current)) {
        markersRef.current = [];
      }

      const buildPopupHtml = (p, index) => {
        const name = p && typeof p.name === 'string' ? p.name.trim() : '';
        if (name) {
          return `
            <div style="font-family: Arial, sans-serif; padding: 8px; min-width: 150px; background: white; border-radius: 4px; box-shadow: 0 2px 10px rgba(0,0,0,0.1);">
              <strong style="color: #333;">${name}</strong>
              ${p && p.notes ? `<br><small style=\"color: #666;\">${p.notes}</small>` : ''}
            </div>
          `;
        }
        return `
          <div style=\"font-family: Arial, sans-serif; padding: 8px; min-width: 150px; background: white; border-radius: 4px; box-shadow: 0 2px 10px rgba(0,0,0,0.1);\">
            <strong style=\"color: #333;\">Resolving address...</strong>
          </div>
        `;
      };

      routePoints.forEach((point, index) => {
        const hasCoords = point && Array.isArray(point.coordinates) && point.coordinates.length === 2;
        let marker = markersRef.current[index];
        let popup = popupRefs.current.get(index);

        // Remove marker if point lost coordinates
        if (!hasCoords) {
          if (marker && marker.remove) {
            try { marker.remove(); } catch (e) {}
          }
          markersRef.current[index] = undefined;
          if (popup) {
            try { popup.remove(); } catch (e) {}
            popupRefs.current.delete(index);
          }
          return;
        }

        // Create marker if missing
        if (!marker) {
          try {
            marker = new mapboxgl.Marker({ color: '#FF5A5F', scale: 1.2 })
              .setLngLat(point.coordinates)
              .addTo(mapRef.current);

            // Create and attach popup
            popup = new mapboxgl.Popup({
              offset: 25,
              anchor: 'bottom',
              closeButton: true,
              closeOnClick: false,
              className: 'route-point-popup'
            })
              .setHTML(buildPopupHtml(point, index))
              .setLngLat(point.coordinates);

            marker.setPopup(popup);
            markersRef.current[index] = marker;
            popupRefs.current.set(index, popup);

            // Ensure marker click opens popup and does not bubble to map
            try {
              const el = marker.getElement();
              if (el) {
                el.addEventListener('click', (evt) => {
                  try { evt.stopPropagation(); } catch (_) {}
                  try { evt.preventDefault(); } catch (_) {}
                  try { marker.togglePopup(); } catch (_) {}
                });
              }
            } catch (_) {}

            // On popup open, resolve address if needed and update in place
            popup.on('open', async () => {
              try { console.debug(`[RouteCreation] Popup opened for marker ${index}`); } catch (_) {}
              try {
                const currentPoint = routePoints[index];
                const currentHasName = currentPoint && typeof currentPoint.name === 'string' && currentPoint.name.trim() !== '';
                if (currentHasName) return;
                if (geocodingInProgress.current.has(index)) return;
                geocodingInProgress.current.add(index);
                popup.setHTML(buildPopupHtml({ ...currentPoint, name: '' }, index));
                const locationData = await reverseGeocode(currentPoint.coordinates[1], currentPoint.coordinates[0]);
                if (locationData && locationData.name) {
                  const updatedPoints = [...routePoints];
                  if (updatedPoints[index]) {
                    updatedPoints[index].name = locationData.name;
                    setRoutePoints(updatedPoints);
                    // Update popup content in place (stay open)
                    popup.setHTML(buildPopupHtml(updatedPoints[index], index));
                  }
                }
              } catch (error) {
                console.error(`❌ Geocoding failed for point ${index + 1}:`, error);
                popup.setHTML(`
                  <div style=\"font-family: Arial, sans-serif; padding: 8px; min-width: 150px; background: white; border-radius: 4px; box-shadow: 0 2px 10px rgba(0,0,0,0.1);\">
                    <strong style=\"color: #333;\">Location ${index + 1}</strong>
                    ${point && point.notes ? `<br><small style=\"color: #666;\">${point.notes}</small>` : ''}
                  </div>
                `);
              } finally {
                geocodingInProgress.current.delete(index);
              }
            });

          } catch (e) {
            console.warn('Error adding marker:', e);
          }
        } else {
          // Update existing marker position
          try { marker.setLngLat(point.coordinates); } catch (_) {}
          // Ensure popup exists
          if (!popup) {
            try {
              popup = new mapboxgl.Popup({
                offset: 25,
                anchor: 'bottom',
                closeButton: true,
                closeOnClick: false,
                className: 'route-point-popup'
              })
                .setHTML(buildPopupHtml(point, index))
                .setLngLat(point.coordinates);
              marker.setPopup(popup);
              popupRefs.current.set(index, popup);
            } catch (_) {}
          } else {
            // Update popup content if name/notes changed; keep it open
            try { popup.setLngLat(point.coordinates); } catch (_) {}
            try { popup.setHTML(buildPopupHtml(point, index)); } catch (_) {}
          }
        }
      });

      // Remove any leftover markers beyond current points length
      for (let i = routePoints.length; i < markersRef.current.length; i++) {
        const m = markersRef.current[i];
        if (m && m.remove) {
          try { m.remove(); } catch (e) {}
        }
        markersRef.current[i] = undefined;
        const p = popupRefs.current.get(i);
        if (p) {
          try { p.remove(); } catch (e) {}
          popupRefs.current.delete(i);
        }
      }

    } catch (e) {
      console.error('Error rendering markers:', e);
    }
  }, [routePoints, isPageVisible]);

  // Route generation functions
  const generateRoute = async () => {
    try {
      setGeneratingRoute(true);
      
      // Filter points with coordinates
      const validPoints = routePoints.filter(point => point.coordinates && Array.isArray(point.coordinates));
      
      if (validPoints.length < 2) {
        alert('At least 2 points with coordinates are required to generate a route');
        return;
      }
      
      const geometry = await RouteGenerationService.generateRoutePath(validPoints, transportMode);
      setRouteGeometry(geometry);
      setRouteLegDurations(Array.isArray(geometry.legDurations) ? geometry.legDurations : []);
      // Keep planner in same view; no preview panel
      setRoutePreviewMode(false);
      
      // Draw the generated route on the map
      drawGeneratedRoute(geometry);
      
    } catch (error) {
      console.error('Error generating route:', error);
      alert(`Failed to generate route: ${error.message}`);
    } finally {
      setGeneratingRoute(false);
    }
  };

  const drawGeneratedRoute = (geometry) => {
    if (!mapRef.current || !geometry) return;
    
    try {
      // Remove existing route line if any
      if (mapRef.current.getSource('generated-route')) {
        mapRef.current.removeLayer('generated-route-line');
        mapRef.current.removeSource('generated-route');
      }
      
      // Add the generated route line
      mapRef.current.addSource('generated-route', {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: geometry.geometry
        }
      });
      
      mapRef.current.addLayer({
        id: 'generated-route-line',
        type: 'line',
        source: 'generated-route',
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': '#28a745',
          'line-width': 6,
          'line-opacity': 0.8
        }
      });
      
      // Fit map to route bounds
      const bounds = new mapboxgl.LngLatBounds();
      geometry.geometry.coordinates.forEach(coord => bounds.extend(coord));
      mapRef.current.fitBounds(bounds, { padding: 50, maxZoom: 15 });
      
    } catch (error) {
      console.error('Error drawing generated route:', error);
    }
  };

  // Auto-generate route when 2+ points are set, clear when fewer than 2
  useEffect(() => {
    const validPoints = routePoints.filter(point => point.coordinates && Array.isArray(point.coordinates));
    
    if (validPoints.length >= 2 && !routeGeometry && !generatingRoute) {
      generateRoute();
    } else if (validPoints.length < 2) {
      // Clear route when fewer than 2 points
      setRouteGeometry(null);
      setRouteLegDurations([]);
      setRoutePreviewMode(false);
      clearRouteFromMap();
    }
  }, [routePoints]);

  // Re-generate when transport mode changes and 2+ points exist
  useEffect(() => {
    const validPoints = routePoints.filter(point => point.coordinates && Array.isArray(point.coordinates));
    if (validPoints.length >= 2 && !generatingRoute) {
      generateRoute();
    }
  }, [transportMode]);

  const saveGeneratedRoute = async () => {
    try {
      setSavingRoute(true);
      
      if (!userId) throw new Error('User not logged in');
      if (!routeName || !routeName.trim()) {
        alert('Please enter a route name before publishing.');
        return;
      }
      
      const validPoints = routePoints.filter(point => point.coordinates && Array.isArray(point.coordinates));
      if (validPoints.length < 2) {
        alert('Add at least 2 points to save a generated route.');
        return;
      }
      
      // Aggregate route-level tags and cost from current points
      const routeTags = Array.from(new Set((routePoints || []).flatMap(p => Array.isArray(p?.tags) ? p.tags : [])));
      const routeCostCents = (routePoints || []).reduce((sum, p) => sum + Math.max(0, Math.round(Number(p?.costCents) || 0)), 0);

      const routeData = {
        name: routeName,
        is_private: isPrivate,
        user_id: userId,
        status: 'active',
        transport_mode: transportMode,
        tags: routeTags,
        cost_cents: routeCostCents,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      
      const result = await RouteGenerationService.generateAndSaveCompleteRoute(
        routeData, 
        validPoints, 
        transportMode
      );

      // Persist collaborators for generated route flow
      if (collaborators && collaborators.length > 0 && result?.route?.id) {
        for (const c of collaborators) {
          try { await addCollaborator(result.route.id, c.user_id, c.role || 'viewer'); } catch (_) {}
        }
      }
      
      alert('Route published successfully!');
      try { if (typeof onCreated === 'function') onCreated(result?.route || null); } catch (_) {}
      handleCancel(); // This will reset the parent component state
      
    } catch (error) {
      console.error('Error saving generated route:', error);
      alert(`Failed to publish route: ${error?.message || 'Unknown error'}`);
    } finally {
      setSavingRoute(false);
    }
  };

  // Helper function to clear route from map
  const clearRouteFromMap = () => {
    if (mapRef.current && mapRef.current.getSource('generated-route')) {
      try {
        mapRef.current.removeLayer('generated-route-line');
        mapRef.current.removeSource('generated-route');
      } catch (e) {
        console.warn('Error removing route from map:', e);
      }
    }
  };

  const cancelRouteGeneration = () => {
    setRoutePreviewMode(false);
    setRouteGeometry(null);
    clearRouteFromMap();
  };

  const saveDraft = () => {
    console.log('Saving draft:', { routeName, routePoints, isPrivate });
    // Implement save draft logic
  };

  // Total time calculation (travel + stay)
  const totalTimes = React.useMemo(() => {
    try {
      const totalTravelSec = Array.isArray(routeLegDurations)
        ? routeLegDurations.reduce((sum, s) => sum + (Number(s) || 0), 0)
        : 0;
      const totalStaySec = Array.isArray(routePoints)
        ? routePoints.reduce((sum, p) => sum + ((Number(p?.stayDuration) || 0) * 60), 0)
        : 0;
      const totalSec = totalTravelSec + totalStaySec;
      const format = (sec) => RouteGenerationService.formatDuration(Math.max(0, Math.round(sec || 0)));
      return {
        travelSec: totalTravelSec,
        staySec: totalStaySec,
        totalSec,
        travelText: format(totalTravelSec),
        stayText: format(totalStaySec),
        totalText: format(totalSec)
      };
    } catch (_) {
      return { travelSec: 0, staySec: 0, totalSec: 0, travelText: '0m', stayText: '0m', totalText: '0m' };
    }
  }, [routeLegDurations, routePoints, transportMode]);

  // Total distance in miles (from generated geometry when available; otherwise approximate via haversine)
  const haversineMeters = (a, b) => {
    try {
      const toRad = (d) => (d * Math.PI) / 180;
      const [lng1, lat1] = a || [];
      const [lng2, lat2] = b || [];
      if (![lng1, lat1, lng2, lat2].every((n) => typeof n === 'number' && isFinite(n))) return 0;
      const R = 6371000;
      const dLat = toRad(lat2 - lat1);
      const dLng = toRad(lng2 - lng1);
      const s1 = Math.sin(dLat / 2);
      const s2 = Math.sin(dLng / 2);
      const aVal = s1 * s1 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * s2 * s2;
      const c = 2 * Math.atan2(Math.sqrt(aVal), Math.sqrt(1 - aVal));
      return R * c;
    } catch (_) {
      return 0;
    }
  };
  const totalDistanceMeters = React.useMemo(() => {
    try {
      const geomMeters = Number(routeGeometry?.distance);
      if (isFinite(geomMeters) && geomMeters > 0) return geomMeters;
      const coords = (routePoints || [])
        .map((p) => (Array.isArray(p?.coordinates) ? p.coordinates : null))
        .filter(Boolean);
      let sum = 0;
      for (let i = 1; i < coords.length; i++) sum += haversineMeters(coords[i - 1], coords[i]);
      return sum;
    } catch (_) {
      return 0;
    }
  }, [routeGeometry, routePoints]);
  const totalMilesText = React.useMemo(() => {
    const miles = Number(totalDistanceMeters || 0) / 1609.344;
    if (!isFinite(miles) || miles <= 0) return '0 mi';
    const digits = miles >= 100 ? 0 : 1;
    return `${miles.toFixed(digits)} mi`;
  }, [totalDistanceMeters]);

  const publishRoute = async () => {
    try {
      if (!Array.isArray(routePoints)) {
        alert('Internal error: routePoints is not an array.');
        return;
      }
      
      if (!userId) throw new Error('User not logged in');
      if (!routeName || !routeName.trim()) {
        alert('Please enter a route name before publishing.');
        return;
      }
      
      const safeRoutePoints = routePoints.filter(Boolean);
      // Optional: at least one point makes sense for Points Only publish
      if (safeRoutePoints.filter(p => Array.isArray(p?.coordinates)).length === 0) {
        alert('Please add at least one point with coordinates before publishing.');
        return;
      }
      
      // Ensure session user matches owner to satisfy RLS insert policy
      try {
        const { data: { user: sessionUser } } = await supabase.auth.getUser();
        if (!sessionUser || sessionUser.id !== userId) {
          console.warn('[RouteCreation] Session user mismatch with owner', { sessionUserId: sessionUser?.id, userId });
        }
      } catch (e) {
        console.warn('[RouteCreation] Could not verify session user before insert', e);
      }
      
      // Insert the route
      const routeData = {
        name: routeName,
        is_private: isPrivate,
        user_id: userId,
        status: 'active',
        transport_mode: transportMode,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      
      const result = await createRoute(routeData);
      const route = result;
      
      if (!route || !route.id) throw new Error('Failed to create route');

      // Insert route points
      const pointsToInsert = safeRoutePoints.map((point, idx) => {
        const tagsArr = Array.isArray(point.tags) ? point.tags : [];
        // Compute time window minutes
        const startMin = parseTimeLabelToMinutes(point.stayStart);
        const endMin = parseTimeLabelToMinutes(point.stayEnd);
        const overnight = (startMin != null && endMin != null) ? (endMin < startMin) : false;
        return {
          route_id: route.id,
          point_order: idx,
          name: point.name || '',
          location_search: point.location || '',
          latitude: point.coordinates ? point.coordinates[1] : null,
          longitude: point.coordinates ? point.coordinates[0] : null,
          cost_cents: Math.max(0, Math.round(Number(point?.costCents) || 0)),
          stay_duration: Math.max(0, Math.round(Number(point?.stayDuration) || 0)),
          stay_start_min: startMin,
          stay_end_min: endMin,
          overnight,
          tags: tagsArr,
          notes: point.notes || '',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };
      });
      
      const { error: pointsError } = await supabase.from('route_points').insert(pointsToInsert);
      if (pointsError) throw pointsError;

      // Persist collaborators for points-only flow
      if (collaborators && collaborators.length > 0) {
        for (const c of collaborators) {
          try { await addCollaborator(route.id, c.user_id, c.role || 'viewer'); } catch (_) {}
        }
      }

      alert('Route published successfully!');
      try { if (typeof onCreated === 'function') onCreated(route || null); } catch (_) {}
      handleCancel(); // This will reset the parent component state
    } catch (error) {
      console.error('Error publishing route:', error);
      alert(`Failed to publish route: ${error?.message || 'Unknown error'}`);
    }
  };

  const saveEditedRoute = async () => {
    try {
      if (!initialRoute?.id) throw new Error('Missing route id');
      if (!routeName || !routeName.trim()) {
        alert('Please enter a route name before saving.');
        return;
      }
      const safeRoutePoints = (routePoints || []).filter(Boolean);
      if (safeRoutePoints.filter(p => Array.isArray(p?.coordinates)).length === 0) {
        alert('Please add at least one point with coordinates before saving.');
        return;
      }

      // Aggregate route-level tags and cost
      const routeTags = Array.from(new Set((safeRoutePoints || []).flatMap(p => Array.isArray(p?.tags) ? p.tags : [])));
      const routeCostCents = (safeRoutePoints || []).reduce((sum, p) => sum + Math.max(0, Math.round(Number(p?.costCents) || 0)), 0);

      await updateRoute(initialRoute.id, {
        name: routeName,
        is_private: isPrivate,
        transport_mode: transportMode,
        tags: routeTags,
        cost_cents: routeCostCents,
        updated_at: new Date().toISOString(),
      });

      // Replace points
      await supabase.from('route_points').delete().eq('route_id', initialRoute.id);
      const pointsToInsert = safeRoutePoints.map((point, idx) => {
        const tagsArr = Array.isArray(point.tags) ? point.tags : [];
        const startMin = parseTimeLabelToMinutes(point.stayStart);
        const endMin = parseTimeLabelToMinutes(point.stayEnd);
        const overnight = (startMin != null && endMin != null) ? (endMin < startMin) : false;
        return {
          route_id: initialRoute.id,
          point_order: idx,
          name: point.name || '',
          location_search: point.location || '',
          latitude: point.coordinates ? point.coordinates[1] : null,
          longitude: point.coordinates ? point.coordinates[0] : null,
          cost_cents: Math.max(0, Math.round(Number(point?.costCents) || 0)),
          stay_duration: Math.max(0, Math.round(Number(point?.stayDuration) || 0)),
          stay_start_min: startMin,
          stay_end_min: endMin,
          overnight,
          tags: tagsArr,
          notes: point.notes || '',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };
      });
      if (pointsToInsert.length > 0) await supabase.from('route_points').insert(pointsToInsert);

      // callback to parent
      if (typeof onSaveEdit === 'function') onSaveEdit({ ...initialRoute, name: routeName, is_private: isPrivate, tags: routeTags, cost_cents: routeCostCents }, safeRoutePoints);
      alert('Route saved successfully!');
      if (typeof onCancel === 'function') onCancel();
    } catch (error) {
      console.error('Error saving route:', error);
      alert(`Failed to save route: ${error?.message || 'Unknown error'}`);
    }
  };

  // Update current step based on form state
  useEffect(() => {
    if (routeName.trim() || routePoints.length > 0) {
      setCurrentStep(2);
    } else {
      setCurrentStep(1);
    }
  }, [routeName, routePoints]);

  const toggleSection = (sectionName) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };

  return (
    <div className="route-planner">
      {/* Tab Navigation */}
      <div className="tab-navigation">
        {tabs.map(tab => (
          <button
            key={tab}
            className={`tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
        <button onClick={handleCancel} className="cancel-btn">Cancel</button>
        {mode === 'create' && (
          <button onClick={publishRoute} className="publish-btn" style={{ marginLeft: 12 }}>Publish</button>
        )}
      </div>

      <div className="planner-content" style={{ height: 'calc(100vh - 120px)', minHeight: '500px', maxHeight: 'none', overflow: 'visible', padding: '24px', display: 'flex', gap: '24px' }}>
        {activeTab === 'Plan' && (
          <>
                      {/* Progress Steps - Vertical Navbar */}
          <div className="progress-navbar">
            <RouteProgressIndicator 
              currentStep={currentStep} 
              totalSteps={2} 
              onStepClick={handleStepClick}
              pointsSummary={(routePoints || []).map((p, idx) => {
                const nm = (p?.name || '').trim();
                if (nm) return nm.length > 60 ? nm.slice(0, 57) + '…' : nm;
                if (Array.isArray(p?.coordinates)) {
                  const [lng, lat] = p.coordinates;
                  const f = (n) => (typeof n === 'number' && isFinite(n) ? n.toFixed(4) : '—');
                  return `Point ${idx + 1} (${f(lat)}, ${f(lng)})`;
                }
                return `Point ${idx + 1}`;
              })}
              title={mode === 'edit' ? 'Edit Route' : 'Create Your Route'}
            />
          </div>

            {/* Left Panel - Enhanced Form */}
            <div className="left-panel" style={{ height: '100%', minHeight: '400px', maxHeight: 'none', overflow: 'visible', display: 'flex', flexDirection: 'column' }}>
              <div className="route-form" style={{ height: '100%', minHeight: '400px', maxHeight: 'none', overflowY: 'auto', overflowX: 'visible', padding: '20px', paddingBottom: '40px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Route Name Section */}
                                  <div className="route-name-section" ref={routeNameSectionRef}>
                    <div className="route-name-header">
                      <div className="route-name-label-group">
                        <label className="route-name-label">Route Name</label>
                        <div className="route-date-range">
                          <input
                            type="date"
                            className="route-date-input"
                            value={routeStartDate || routeDate}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (routeEndDate) {
                                setRouteStartDate(v);
                                if (routeEndDate && v && routeEndDate < v) setRouteEndDate(v);
                                setRouteDate('');
                              } else {
                                setRouteDate(v);
                              }
                            }}
                            placeholder="Start date"
                          />
                          <span className="date-sep">–</span>
                          <input
                            type="date"
                            className="route-date-input"
                            value={routeEndDate}
                            min={routeStartDate || routeDate || undefined}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v) {
                                // Promote single date into range when end is set
                                if (!routeStartDate && routeDate) setRouteStartDate(routeDate);
                                setRouteEndDate(v);
                                setRouteDate('');
                              } else {
                                setRouteEndDate('');
                              }
                            }}
                            placeholder="End date"
                          />
                        </div>
                      </div>
                      <div className="route-name-meta">
                        <div className="creator-avatar" title={creatorUsername || 'Creator'}>
                          {creatorAvatarUrl ? (
                            <img src={creatorAvatarUrl} alt={creatorUsername || 'Creator'} />
                          ) : (
                            <span>{(creatorUsername || (typeof userId === 'string' ? userId : 'U')).toString().charAt(0).toUpperCase()}</span>
                          )}
                        </div>
                        <button
                          type="button"
                          className="add-collaborator-btn"
                          title="Add collaborators"
                          onClick={() => setCollabModalOpen(true)}
                        >
                          +
                        </button>
                      </div>
                    </div>
                    <input
                      type="text"
                      value={routeName}
                      onChange={(e) => setRouteName(e.target.value)}
                      placeholder="Enter a memorable route name..."
                      className="route-name-input"
                    />
                  </div>

                {/* Points Section */}
                <div className="form-section points-form-section" ref={pointsSectionRef}>
                  <div 
                    className="section-header"
                  >
                    <div className="section-title">
                      <span className="section-icon">📍</span>
                      <span>Route Points {routePoints.filter(p => p.coordinates).length}</span>
                    </div>
                    <div className="section-indicator">
                      {routePoints.filter(p => p.coordinates).length >= 2 ? '✓' : routePoints.length}
                    </div>
                  </div>
                  <div 
                    className={`section-content expanded`}
                    style={{
                      maxHeight: 'none',
                      overflow: 'visible',
                      marginBottom: '20px'
                    }}
                  >
                    <div className="points-section" style={{ maxHeight: 'none', overflow: 'visible', marginBottom: '20px' }}>
                      {routePoints.map((point, index) => {
                        const isActive = !point.coordinates && routePoints.slice(0, index).every(p => p.coordinates);
                        const isCompleted = !!point.coordinates;
                        
                        return (
                          <React.Fragment key={index}>
                            <div 
                              className={`point-card ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
                            >

                            {/* Point Header */}
                            <div className="point-header">
                              <div className="point-number-badge">
                                {index + 1}
                              </div>
                              <div className="point-status">
                                <div className="header-inline-controls">
                                    <div className="location-input-group">
                                      <input
                                        type="text"
                                        placeholder="Search for a location..."
                                        value={point.location}
                                        onChange={(e) => handleLocationChange(index, e.target.value)}
                                        onFocus={() => setShowSuggestions({ ...showSuggestions, [index]: true })}
                                        onBlur={() => setTimeout(() => setShowSuggestions({ ...showSuggestions, [index]: false }), 200)}
                                        className="location-input"
                                      />
                                      {showSuggestions[index] && searchSuggestions[index] && searchSuggestions[index].length > 0 && (
                                        <div className="suggestions-dropdown">
                                          {searchSuggestions[index].map((suggestion, suggestionIndex) => (
                                            <div
                                              key={suggestionIndex}
                                              className="suggestion-item"
                                              onClick={() => selectSuggestion(index, suggestion)}
                                            >
                                              <span className="suggestion-icon">📍</span>
                                              <div className="suggestion-content">
                                                <div className="suggestion-name">{suggestion.place_name.split(',')[0]}</div>
                                                <div className="suggestion-address">{suggestion.place_name}</div>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                      {/* Controls below input (Tags + Stay) */}
                                      <div className="below-input-row">
                                        <div className="tags-inline" ref={el => (tagsInlineRefs.current[index] = el)}>
                                          <button
                                            type="button"
                                            className="tags-compact-btn"
                                            aria-label="Label this stop with tags"
                                            data-tooltip="Label this stop with tags"
                                            onClick={() => toggleTagsDropdown(index)}
                                          >
                                            Tags +
                                          </button>
                                          {point.tagsDropdownOpen && (
                                            <div className="tags-dropdown-list">
                                              {[
                                                'adventure','travel','food','coffee','museum','nature','shopping','historic','entertainment','hiking','beach','nightlife'
                                              ].map((tag) => {
                                                const isSelected = Array.isArray(point.tags) && point.tags.includes(tag);
                                                return (
                                                  <div
                                                    key={tag}
                                                    className={`tag-option ${isSelected ? 'selected' : ''}`}
                                                    onClick={() => toggleTag(index, tag)}
                                                  >
                                                    <span className="tag-option-icon">{isSelected ? '✓' : '○'}</span>
                                                    {tag}
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          )}
                                      </div>
                                        <div className={`stay-inline ${point?.stayDropUp ? 'dropup' : ''}`} ref={el => (stayInlineRefs.current[index] = el)}>
                                          <button
                                            type="button"
                                            className="stay-compact-btn"
                                            aria-label="Set time for this stop"
                                            data-tooltip="Set time for this stop"
                                            onClick={() => {
                                              const willOpen = !routePoints[index]?.stayDropdownOpen;
                                              // Decide if we should open dropdown upwards based on viewport space
                                              let openUp = false;
                                              try {
                                                const el = stayInlineRefs.current[index];
                                                if (el) {
                                                  const rect = el.getBoundingClientRect();
                                                  const estimatedHeight = 330; // approx dropdown height
                                                  const spaceBelow = (window.innerHeight || 0) - rect.bottom;
                                                  openUp = spaceBelow < estimatedHeight;
                                                }
                                              } catch (_) {}
                                              const updated = routePoints.map((p, i) => {
                                                if (!p) return p;
                                                if (i === index) {
                                                  return { 
                                                    ...p, 
                                                    stayDropdownOpen: willOpen, 
                                                    tagsDropdownOpen: false,
                                                    stayDropUp: openUp,
                                                    stayStart: p.stayStart || null,
                                                    stayEnd: p.stayEnd || null
                                                  };
                                                }
                                                return { ...p, stayDropdownOpen: false };
                                              });
                                              setRoutePoints(updated);
                                              if (willOpen) {
                                                try {
                                                  const el = stayInlineRefs.current[index];
                                                  if (el && typeof el.scrollIntoView === 'function') {
                                                    requestAnimationFrame(() => {
                                                      try { el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' }); } catch (_) {}
                                                    });
                                                  }
                                                } catch (_) {}
                                              }
                                            }}
                                          >
                                            {(() => {
                                              const start = point.stayStart;
                                              const end = point.stayEnd;
                                              if (start && end) return `🕒 ${start} – ${end}`;
                                              const m = point.stayDuration || 0; const h = Math.floor(m/60); const mm = m%60; return `Time: ${h>0 ? `${h}h${mm?` ${mm}m`:''}` : `${m||0}m`}`;
                                            })()}
                                          </button>
                                          {point.stayDropdownOpen && (
                                            <div className="stay-dropdown">
                                              {/* stay-chips removed */}
                                              <div className="stay-time-range">
                                                <div className="time-col">
                                                  <label className="time-label">Start time</label>
                                                  <div
                                                    className={`time-display ${point.openStart ? 'open' : ''}`}
                                                    onClick={() => {
                                                      const updated = [...routePoints];
                                                      updated[index].openStart = !point.openStart;
                                                      updated[index].openEnd = false;
                                                      setRoutePoints(updated);
                                                    }}
                                                  >{point.stayStart || 'Select…'}</div>
                                                  {point.openStart && (
                                                    <div className="time-options">
                                                      {timeOptions.map((t) => (
                                                        <div key={`start-opt-${t}`} className="time-option" onClick={() => {
                                                          const updated = [...routePoints];
                                                          updated[index].stayStart = t;
                                                          updated[index].openStart = false;
                                                          const sMin = parseTimeLabelToMinutes(t);
                                                          const eMin = parseTimeLabelToMinutes(updated[index].stayEnd);
                                                          if (sMin != null && eMin != null) {
                                                            let diff = eMin - sMin; if (diff < 0) diff += 24*60;
                                                            updated[index].stayDuration = diff;
                                                          }
                                                          setRoutePoints(updated);
                                                        }}>{t}</div>
                                                      ))}
                                                    </div>
                                                  )}
                                                </div>
                                                <div className="time-sep">–</div>
                                                <div className="time-col">
                                                  <label className="time-label">End time</label>
                                                  <div
                                                    className={`time-display ${point.openEnd ? 'open' : ''}`}
                                                    onClick={() => {
                                                      const updated = [...routePoints];
                                                      updated[index].openEnd = !point.openEnd;
                                                      updated[index].openStart = false;
                                                      setRoutePoints(updated);
                                                    }}
                                                  >{point.stayEnd || 'Select…'}</div>
                                                  {point.openEnd && (
                                                    <div className="time-options">
                                                      {timeOptions.map((t) => (
                                                        <div key={`end-opt-${t}`} className="time-option" onClick={() => {
                                                          const updated = [...routePoints];
                                                          updated[index].stayEnd = t;
                                                          updated[index].openEnd = false;
                                                          const sMin = parseTimeLabelToMinutes(updated[index].stayStart);
                                                          const eMin = parseTimeLabelToMinutes(t);
                                                          if (sMin != null && eMin != null) {
                                                            let diff = eMin - sMin; if (diff < 0) diff += 24*60;
                                                            updated[index].stayDuration = diff;
                                                          }
                                                          setRoutePoints(updated);
                                                        }}>{t}</div>
                                                      ))}
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                              <div className="stay-apply-row">
                                                <button type="button" className="stay-apply" onClick={()=>{
                                                  const updated = [...routePoints];
                                                  updated[index].stayDropdownOpen = false;
                                                  setRoutePoints(updated);
                                                }}>Apply</button>
                                              </div>
                                            </div>
                                          )}
                                      </div>
                                      {/* Cost control */}
                                      <div className={`cost-inline ${point?.costDropUp ? 'dropup' : ''}`} ref={el => (costInlineRefs.current[index] = el)}>
                                        <button
                                          type="button"
                                          className="cost-compact-btn"
                                          aria-label="Add cost for this stop"
                                          data-tooltip="Add cost for this stop"
                                          onClick={() => {
                                            const willOpen = !routePoints[index]?.costDropdownOpen;
                                            // Decide if we should open dropdown upwards based on viewport space
                                            let openUp = false;
                                            try {
                                              const el = costInlineRefs.current[index];
                                              if (el) {
                                                const rect = el.getBoundingClientRect();
                                                const estimatedHeight = 300; // approx dropdown height
                                                const spaceBelow = (window.innerHeight || 0) - rect.bottom;
                                                openUp = spaceBelow < estimatedHeight;
                                              }
                                            } catch (_) {}
                                            const updated = routePoints.map((p, i) => {
                                              if (!p) return p;
                                              if (i === index) {
                                                return {
                                                  ...p,
                                                  costDropdownOpen: willOpen,
                                                  tagsDropdownOpen: false,
                                                  stayDropdownOpen: false,
                                                  costDropUp: openUp,
                                                  _costAmountInput: p.costCents ? (Math.round((p.costCents || 0)) / 100).toFixed(2) : (p._costAmountInput || '')
                                                };
                                              }
                                              return { ...p, costDropdownOpen: false };
                                            });
                                            setRoutePoints(updated);
                                            // If opening, make sure the dropdown is fully visible within the scroll container
                                            if (willOpen) {
                                              try {
                                                const el = costInlineRefs.current[index];
                                                if (el && typeof el.scrollIntoView === 'function') {
                                                  // Use rAF to wait for DOM to paint the dropdown
                                                  requestAnimationFrame(() => {
                                                    try { el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' }); } catch (_) {}
                                                  });
                                                }
                                              } catch (_) {}
                                            }
                                          }}
                                        >
                                          {(() => {
                                            const c = Math.max(0, Math.round(Number(point?.costCents) || 0));
                                            const splitOn = point?.split?.enabled && Array.isArray(point?.split?.participantIds) && point.split.participantIds.length > 1;
                                            const base = c > 0 ? `💵 ${formatCurrency(c)}` : '💵 Cost';
                                            return splitOn ? `${base} • Split` : base;
                                          })()}
                                        </button>
                                        {point.costDropdownOpen && (
                                          <div className="cost-dropdown">
                                            <label className="time-label">Amount</label>
                                            <input
                                              type="text"
                                              inputMode="decimal"
                                              placeholder="$0.00"
                                              value={point._costAmountInput || ''}
                                              onChange={(e) => {
                                                const updated = [...routePoints];
                                                updated[index]._costAmountInput = e.target.value;
                                                setRoutePoints(updated);
                                              }}
                                            />
                                            {/* Split toggle */}
                                            <div className="cost-split-toggle">
                                              <button
                                                type="button"
                                                className="split-link"
                                                onClick={() => {
                                                  const updated = [...routePoints];
                                                  const p = updated[index] || {};
                                                  // initialize defaults
                                                  if (!p.split || !p.split.participantIds) {
                                                    p.split = {
                                                      enabled: false,
                                                      participantIds: [userId].filter(Boolean),
                                                      payerId: userId || null,
                                                      mode: 'equal'
                                                    };
                                                  }
                                                  p.splitOpen = !p.splitOpen;
                                                  updated[index] = { ...p };
                                                  setRoutePoints(updated);
                                                }}
                                              >
                                                {point.splitOpen ? 'Close split' : 'Split cost'}
                                              </button>
                                              {(() => {
                                                const enabled = point?.split?.enabled && Array.isArray(point?.split?.participantIds) && point.split.participantIds.length > 1;
                                                return enabled ? <span className="split-badge">Split</span> : null;
                                              })()}
                                            </div>

                                            {/* Split sheet (equal split) */}
                                            {point.splitOpen && (
                                              <div className="cost-split-sheet">
                                                <div className="split-preview-mode">
                                                  <label className="preview-mode-option">
                                                    <input
                                                      type="radio"
                                                      name={`preview-mode-${index}`}
                                                      checked={point._splitPreviewMode !== 'count'}
                                                      onChange={() => {
                                                        const updated = [...routePoints];
                                                        updated[index]._splitPreviewMode = 'select';
                                                        setRoutePoints(updated);
                                                      }}
                                                    />
                                                    <span>Select people</span>
                                                  </label>
                                                  <label className="preview-mode-option">
                                                    <input
                                                      type="radio"
                                                      name={`preview-mode-${index}`}
                                                      checked={point._splitPreviewMode === 'count'}
                                                      onChange={() => {
                                                        const updated = [...routePoints];
                                                        updated[index]._splitPreviewMode = 'count';
                                                        if (!updated[index]._splitCount || updated[index]._splitCount < 2) updated[index]._splitCount = 2;
                                                        setRoutePoints(updated);
                                                      }}
                                                    />
                                                    <span>By count</span>
                                                  </label>
                                                  {point._splitPreviewMode === 'count' && (
                                                    <div className="preview-count-input">
                                                      <label>People</label>
                                                      <input
                                                        type="number"
                                                        min={1}
                                                        value={Math.max(1, Number(point._splitCount) || 2)}
                                                        onChange={(e) => {
                                                          const updated = [...routePoints];
                                                          updated[index]._splitCount = Math.max(1, Number(e.target.value) || 1);
                                                          setRoutePoints(updated);
                                                        }}
                                                      />
                                                    </div>
                                                  )}
                                                </div>
                                                <div className="split-row">
                                                  <div className="split-col">
                                                    <div className="split-section-label">Participants</div>
                                                    <div className="split-participants">
                                                      {getParticipantCandidates().map(u => {
                                                        const selected = Array.isArray(point?.split?.participantIds) && point.split.participantIds.includes(u.id);
                                                        return (
                                                          <label key={u.id} className={`split-person ${selected ? 'selected' : ''}`}>
                                                            <input
                                                              type="checkbox"
                                                              checked={selected}
                                                              onChange={(e) => {
                                                                const updated = [...routePoints];
                                                                const p = { ...(updated[index] || {}) };
                                                                const s = p.split || { enabled: false, participantIds: [], payerId: userId || null, mode: 'equal' };
                                                                const setIds = new Set(s.participantIds || []);
                                                                if (e.target.Checked) { /* no-op to keep case */ }
                                                                if (e.target.checked) setIds.add(u.id); else setIds.delete(u.id);
                                                                if (setIds.size === 0 && userId) setIds.add(userId); // keep at least you
                                                                s.participantIds = Array.from(setIds);
                                                                // keep payer internal; default to first if needed
                                                                if (!s.participantIds.includes(s.payerId)) s.payerId = s.participantIds[0] || userId || null;
                                                                p.split = s;
                                                                updated[index] = p;
                                                                setRoutePoints(updated);
                                                              }}
                                                            />
                                                            <span className="person-name">{u.isSelf ? 'You' : (u.name || 'User')}</span>
                                                          </label>
                                                        );
                                                      })}
                                                    </div>
                                                  </div>
                                                </div>
                                                <div className="split-row">
                                                  <div className="split-section-label">Shares (equal)</div>
                                                  <div className="split-shares">
                                                    {(() => {
                                                      const s = point?.split || { participantIds: [userId].filter(Boolean), payerId: userId || null };
                                                      const totalCents = (point?._costAmountInput !== undefined && point._costAmountInput !== '')
                                                        ? parseAmountToCents(point._costAmountInput)
                                                        : Math.max(0, Math.round(Number(point?.costCents) || 0));
                                                      if (point._splitPreviewMode === 'count') {
                                                        const count = Math.max(1, Number(point._splitCount) || 2);
                                                        if (count <= 0) return null;
                                                        const base = Math.floor(totalCents / count);
                                                        let remainder = totalCents - base * count;
                                                        return Array.from({ length: count }).map((_, i) => {
                                                          const extra = i === 0 ? remainder : 0;
                                                          const amt = base + extra;
                                                          return (
                                                            <div key={`count-${i}`} className="share-row">
                                                              <span className="person-name">Person {i + 1}</span>
                                                              <span className="share-amt">{formatCurrency(amt)}</span>
                                                            </div>
                                                          );
                                                        });
                                                      } else {
                                                        const shares = computeEqualShares(totalCents, s.participantIds || [], s.payerId);
                                                        return (s.participantIds || []).map(pid => {
                                                          const c = getParticipantCandidates().find(x => x.id === pid);
                                                          const nm = c?.isSelf ? 'You' : (c?.name || pid?.slice(0,6) || 'User');
                                                          const amt = shares[pid] || 0;
                                                          return (
                                                            <div key={pid} className="share-row">
                                                              <span className="person-name">{nm}</span>
                                                              <span className="share-amt">{formatCurrency(amt)}</span>
                                                            </div>
                                                          );
                                                        });
                                                      }
                                                    })()}
                                                  </div>
                                                </div>
                                                <div className="split-actions">
                                                  <button
                                                    type="button"
                                                    className="save-split-btn"
                                                    onClick={() => {
                                                      const updated = [...routePoints];
                                                      const p = { ...(updated[index] || {}) };
                                                      const s = p.split || {};
                                                      const ids = Array.isArray(s.participantIds) && s.participantIds.length > 0 ? s.participantIds : [userId].filter(Boolean);
                                                      p.split = {
                                                        enabled: (ids?.length || 0) > 1,
                                                        participantIds: ids,
                                                        payerId: s.payerId || (ids[0] || userId || null),
                                                        mode: 'equal'
                                                      };
                                                      p.splitOpen = false;
                                                      updated[index] = p;
                                                      setRoutePoints(updated);
                                                    }}
                                                  >Save split</button>
                                                </div>
                                              </div>
                                            )}
                                            <div className="cost-apply-row">
                                              <button type="button" className="cost-apply" onClick={() => {
                                                const updated = [...routePoints];
                                                const cents = parseAmountToCents(updated[index]._costAmountInput);
                                                updated[index].costCents = cents;
                                                updated[index].costDropdownOpen = false;
                                                setRoutePoints(updated);
                                              }}>Apply</button>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    {Array.isArray(point.tags) && point.tags.length > 0 && (
                                        <div className="selected-tags-chips">
                                          {point.tags.map((t) => (
                                            <span key={t} className="tag-chip-small">{t}</span>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                              </div>
                              {routePoints.length > 1 && (
                                <button 
                                  onClick={() => removePoint(index)}
                                  className="remove-point-btn"
                                  title="Remove this point"
                                >
                                  ×
                                </button>
                              )}
                            </div>

                            {/* Point Content */}
                            <div className="point-content">
                              {/* Location Input moved to header */}

                              {/* Point Name Input removed by request */}

                            </div>



                              {/* Tags moved to header; chips now render under the search input */}

                              {/* Notes Input (auto-expanding textarea with counter) */}
                              <div className={`notes-input-group ${Array.isArray(point.tags) && point.tags.length > 0 ? (point.tags.length > 6 ? 'notes-shift-many' : 'notes-shift') : ''}`}>
                                <textarea
                                  className="notes-textarea"
                                  placeholder="Add notes about this stop"
                                  rows={1}
                                  maxLength={500}
                                  value={point.notes || ''}
                                  onChange={(e) => updatePoint(index, 'notes', e.target.value)}
                                  onInput={(e) => {
                                    const el = e.target;
                                    el.style.height = 'auto';
                                    const lineHeight = 20; // must match CSS line-height
                                    const maxLines = 20;
                                    const maxPx = maxLines * lineHeight;
                                    el.style.height = Math.min(el.scrollHeight, maxPx) + 'px';
                                  }}
                                  onBlur={(e) => {
                                    const cleaned = sanitizeNotes(e.target.value);
                                    if (cleaned !== e.target.value) {
                                      updatePoint(index, 'notes', cleaned);
                                    }
                                  }}
                                />
                                <div className="notes-counter">{(point.notes?.length || 0)}/500</div>
                              </div>
                          </div>
                          
                          {/* Travel Segment between points (except after the last point) */}
                          {index < routePoints.length - 1 && (
                            <>
                              <div className="travel-segment-between">
                                <div className="travel-segment-content">
                                  <span className="travel-icon">🚗</span>
                                  <span
                                    className="travel-duration"
                                    onClick={() =>
                                      setOpenLegDirections(prev => ({
                                        ...prev,
                                        [index]: !prev[index]
                                      }))
                                    }
                                  >
                                    {(() => {
                                      const seconds = routeLegDurations[index] || 0;
                                      const minutes = Math.max(1, Math.round(seconds / 60));
                                      return `~${minutes}m travel time (view directions)`;
                                    })()}
                                  </span>
                                </div>
                              </div>
                              {openLegDirections[index] && Array.isArray(routeGeometry?.legs?.[index]?.steps) && (
                                <div className="directions-panel">
                                  <ol className="directions-list">
                                    {routeGeometry.legs[index].steps.map((step, sIdx) => {
                                      const instr = step?.maneuver?.instruction || step?.name || 'Continue';
                                      const distText = RouteGenerationService.formatDistance(Number(step?.distance) || 0);
                                      const durText = RouteGenerationService.formatDuration(Number(step?.duration) || 0);
                                      return (
                                        <li key={sIdx} className="direction-item">
                                          <span className="direction-text">{instr}</span>
                                          <span className="direction-meta">{distText} • {durText}</span>
                                        </li>
                                      );
                                    })}
                                  </ol>
                                </div>
                              )}
                            </>
                          )}
                        </React.Fragment>
                      );
                    })}
                      
                      <button 
                        onClick={addPoint} 
                        className="add-point-btn"
                      >
                        <span className="add-icon">+</span>
                        <span>Add Point</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Debug box removed */}

                {/* Privacy Section */}
                <div className="form-section">
                  <div className="privacy-toggle-group">
                    <div className="privacy-toggle">
                        <input
                          type="checkbox"
                        id="privacy-toggle"
                        checked={isPrivate}
                        onChange={(e) => setIsPrivate(e.target.checked)}
                      />
                      <label htmlFor="privacy-toggle" className="toggle-slider"></label>
                      <label htmlFor="privacy-toggle" className="privacy-label">
                        <span className="privacy-icon">{isPrivate ? '🔒' : '🌐'}</span>
                        {isPrivate ? 'Private Route' : 'Public Route'}
                      </label>
                    </div>
                  </div>
                </div>

                {/* Route Generation Section removed */}

                {/* Route Preview removed */}

                {/* Sticky Action Bar */}
                {!routePreviewMode && (
                  <div className="action-bar">
                    <div style={{ marginRight: 'auto', fontSize: 14, color: '#374151' }}>
                      <strong>Total Time:</strong> {totalTimes.totalText}
                      <span style={{ color: '#6b7280', marginLeft: 12 }}>• Total Miles: {totalMilesText}</span>
                      <span style={{ color: '#6b7280', marginLeft: 12 }}>• Total Cost: {formatCurrency((routePoints || []).reduce((acc, p) => acc + (Math.max(0, Math.round(Number(p?.costCents) || 0))), 0))}</span>
                    </div>
                    {mode === 'create' ? (
                      <>
                        <button onClick={saveDraft} className="save-draft-btn">Save Draft</button>
                        <button onClick={publishRoute} className="publish-btn">Publish Route (Points Only)</button>
                      </>
                    ) : (
                      <button onClick={saveEditedRoute} className="publish-btn">Save Changes</button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Right Panel - Map */}
            <div className="right-panel">
              <div className="route-creation-map" style={{ position: 'relative' }}>
                {/* Temporarily disabled FullscreenMapButton to test */}
                {/* <FullscreenMapButton mapContainerRef={mapContainerRef} /> */}
                <div 
                  ref={mapContainerRef} 
                  className={`route-map ${routePoints.some(p => !p.coordinates) ? 'placing-points' : ''}`}
                ></div>
              </div>
            </div>
          </>
        )}
      </div>
    {/* Collaborators Modal */}
    <RouteCollaboratorsModal
      open={collabModalOpen}
      onClose={() => setCollabModalOpen(false)}
      initialCollaborators={collaborators}
      onSave={(list) => { setCollaborators(list); setCollabModalOpen(false); }}
      ownerUserId={userId}
    />
    </div>
  );
};

export default RouteCreation; 
